import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { del, get, post } from '../api';
import { tr } from '../i18n';
import { useSession } from '../session';
import { ago, Badge, Button, Card, CopyBox, Field, friendlyError, PageHead, useStepUp, useToast } from '../ui';

export function Account() {
  const { me, refresh } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const stepUp = useStepUp(!!me?.user.mfaEnabled);
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: () => get('/api/auth/sessions') });
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '' });
  if (!me) return null;
  return (
    <div>
      <PageHead eyebrow={tr('Account')} title={me.user.name || me.user.email} desc={<span className="mono">{me.user.email}</span>} />
      <div className="grid g2">
        <Card title={tr('Two-step sign-in')}>
          {me.user.mfaEnabled ? (
            <div className="stack">
              <div><Badge tone="ok">{tr('Enabled')}</Badge></div>
              <div className="row">
                <Button onClick={() => stepUp.run(async () => { const r = await post('/api/auth/mfa/recovery-codes'); setCodes(r.recoveryCodes); })}>{tr('New recovery codes')}</Button>
                {!me.isSuperAdmin && <Button variant="danger" onClick={() => stepUp.run(async () => { await post('/api/auth/mfa/disable'); await refresh(); toast(tr('Two-step sign-in turned off')); }).catch((e) => toast(friendlyError(e), true))}>{tr('Disable')}</Button>}
              </div>
            </div>
          ) : setup ? (
            <div className="stack">
              <p style={{ margin: 0 }}>{tr('Open an authenticator app on your phone (such as Google Authenticator or Authy), scan this code, then type the 6 digits it shows.')}</p>
              <div style={{ background: '#fff', padding: 12, width: 'fit-content', borderRadius: 8 }}><QRCodeSVG value={setup.otpauthUri} size={168} /></div>
              <CopyBox label={tr('Or enter this key manually')} text={setup.secret} />
              <Field label={tr('Code')}><input type="text" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} /></Field>
              <Button variant="primary" onClick={async () => { try { const r = await post('/api/auth/mfa/enable', { code }); setCodes(r.recoveryCodes); setSetup(null); await refresh(); } catch (e) { toast(friendlyError(e), true); } }}>{tr('Enable')}</Button>
            </div>
          ) : (
            <div className="stack">
              <div><Badge tone="warn">{tr('Off')}</Badge></div>
              <p className="muted" style={{ margin: 0 }}>{me.isSuperAdmin ? tr('Required for operators.') : tr('Recommended: even if someone learns your password, they cannot sign in without your phone.')}</p>
              <div><Button variant="primary" onClick={async () => setSetup(await post('/api/auth/mfa/setup'))}>{tr('Set up')}</Button></div>
            </div>
          )}
          {codes && (
            <div className="notice warn" style={{ marginTop: 14 }}>
              <strong>{tr('Save these recovery codes now — each works once and they are not shown again.')}</strong>
              <div className="codebox" style={{ columns: 2, marginTop: 8 }}>{codes.join('\n')}</div>
            </div>
          )}
        </Card>
        <Card title={tr('Password')}>
          {me.user.googleLinked && <p style={{ marginTop: 0 }}><Badge tone="ok">{tr('Connected to Google')}</Badge> <span className="muted">{tr('You can sign in with your Google account.')}</span></p>}
          {me.user.hasPassword ? (
            <>
              <Field label={tr('Current password')}><input type="password" autoComplete="current-password" value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} /></Field>
              <Field label={tr('New password')} hint={tr('At least 12 characters. You will be signed out on your other devices.')}><input type="password" autoComplete="new-password" value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} /></Field>
              <Button variant="primary" onClick={async () => { try { await post('/api/auth/password/change', pw); setPw({ currentPassword: '', newPassword: '' }); toast(tr('Password changed')); qc.invalidateQueries({ queryKey: ['sessions'] }); } catch (e) { toast(friendlyError(e), true); } }}>{tr('Change password')}</Button>
            </>
          ) : (
            <>
              <p className="muted" style={{ marginTop: 0 }}>{tr('You sign in with Google, so you have no ZeroGap password. Add one to also sign in with your email address.')}</p>
              <Field label={tr('New password')} hint={tr('At least 12 characters.')}><input type="password" autoComplete="new-password" value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} /></Field>
              <Button variant="primary" onClick={() => stepUp.run(async () => { await post('/api/auth/password/change', { newPassword: pw.newPassword }); setPw({ currentPassword: '', newPassword: '' }); toast(tr('Password added')); await refresh(); qc.invalidateQueries({ queryKey: ['sessions'] }); }).catch((e) => toast(friendlyError(e), true))}>{tr('Add a password')}</Button>
            </>
          )}
        </Card>
      </div>
      <Card title={tr('Where you are signed in')} pad={false}>
        <table>
          <tbody>
            {sessions.data?.sessions.map((s: any) => (
              <tr key={s.id}>
                <td className="small"><bdi>{s.userAgent?.slice(0, 80)}</bdi><div className="muted mono">{s.ip}</div></td>
                <td className="small">{tr('signed in {signedIn} · active {active}', { signedIn: ago(s.createdAt), active: ago(s.lastSeenAt) })}</td>
                <td className="right">{s.current ? <Badge tone="info">{tr('This device')}</Badge> : <Button size="sm" onClick={async () => { await del(`/api/auth/sessions/${s.id}`); qc.invalidateQueries({ queryKey: ['sessions'] }); }}>{tr('Sign out')}</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {stepUp.dialog}
    </div>
  );
}
